/**
 * Custom Workflows are gated by **LaunchDarkly** (`customworkflows` or camelCase `customWorkflows`
 * if the flag key uses hyphens in LD).
 *
 * Optional **tightening** with env allowlists (Create React App — prefix with REACT_APP_):
 * - REACT_APP_CUSTOM_WORKFLOWS_ALLOWLIST_MSP_IDS: comma-separated MSP ids, e.g. "12,34"
 * - REACT_APP_CUSTOM_WORKFLOWS_ALLOWLIST_EMAILS: comma-separated emails, case-insensitive
 *
 * If allowlists are **unset**: LD flag **on** is enough to show Custom Workflows.
 * If **either** allowlist is non-empty: LD must be **on** and the user must match **MSP id OR email**.
 */

function parseCsvList(raw: string | undefined): string[] {
  if (!raw?.trim()) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function envString(key: string): string | undefined {
  const v = process.env[key];
  return typeof v === "string" ? v : undefined;
}

/** True when deploy has configured at least one allowlist entry. */
export function isCustomWorkflowsAllowlistConfigured(): boolean {
  const msp = parseCsvList(
    envString("REACT_APP_CUSTOM_WORKFLOWS_ALLOWLIST_MSP_IDS")
  );
  const emails = parseCsvList(
    envString("REACT_APP_CUSTOM_WORKFLOWS_ALLOWLIST_EMAILS")
  );
  return msp.length > 0 || emails.length > 0;
}

export function isCustomWorkflowsAllowedForUser(opts: {
  mspId: number | string | null | undefined;
  email: string | null | undefined;
}): boolean {
  const mspAllow = parseCsvList(
    envString("REACT_APP_CUSTOM_WORKFLOWS_ALLOWLIST_MSP_IDS")
  );
  const emailAllow = parseCsvList(
    envString("REACT_APP_CUSTOM_WORKFLOWS_ALLOWLIST_EMAILS")
  );

  if (mspAllow.length === 0 && emailAllow.length === 0) {
    return true;
  }

  const mspStr = String(opts.mspId ?? "").trim();
  if (mspAllow.some((id) => String(id).trim() === mspStr)) {
    return true;
  }

  const em = String(opts.email ?? "").trim().toLowerCase();
  if (em && emailAllow.some((e) => e.trim().toLowerCase() === em)) {
    return true;
  }

  return false;
}

/** True when LD serves the Custom Workflows boolean flag on for this context. */
export function isCustomWorkflowsLaunchDarklyEnabled(
  flags: Record<string, unknown> | undefined
): boolean {
  if (!flags || typeof flags !== "object") {
    return false;
  }
  const f = flags as Record<string, unknown>;
  return (
    f.customWorkflows === true ||
    f.customworkflows === true ||
    f["custom-workflows"] === true
  );
}

/**
 * Sidebar, `/custom_workflows` routes, and chat workflow picker: LD flag on; if allowlists
 * are set, user must also match.
 */
export function isCustomWorkflowsNavVisible(
  flags: Record<string, unknown> | undefined,
  opts: {
    mspId: number | string | null | undefined;
    email: string | null | undefined;
  }
): boolean {
  if (!isCustomWorkflowsLaunchDarklyEnabled(flags)) {
    return false;
  }
  if (!isCustomWorkflowsAllowlistConfigured()) {
    return true;
  }
  return isCustomWorkflowsAllowedForUser(opts);
}
